import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserEdit } from '@/utils/permissions';
import ListLoadError from '@/components/ListLoadError';
import { 
  CreditCard, Plus, Search, Filter, Download, Edit2, Eye, X, 
  RefreshCw, CheckCircle, AlertCircle, Clock, Calendar, DollarSign,
  FileText, Loader2, ChevronDown, Save, Trash2, ChevronRight,
  ShieldAlert, RotateCcw, Wallet
} from 'lucide-react';

// Searchable Dropdown Component
function SearchableDropdown({ 
  label, value, options, onChange, placeholder, displayField, valueField,
  required, disabled, testId 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? selectedOption[displayField] : '';
  
  const filteredOptions = options.filter(opt => 
    opt[displayField]?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between transition ${
          disabled 
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-300 hover:border-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-100'
        }`}
        data-testid={testId}
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">No results found</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onChange(opt[valueField], opt);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-amber-50 flex items-center justify-between"
                >
                  <span>{opt[displayField]}</span>
                  {value === opt[valueField] && <CheckCircle size={14} className="text-amber-500" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ExportPaymentsDropdown() {
  const { API } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleExport = async (bucket) => {
    setDownloading(bucket);
    setOpen(false);
    try {
      const resp = await axios.get(`${API}/stores/payments/export`, {
        params: { bucket },
        responseType: 'blob',
        timeout: 120000,
      });
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `stores-payments-${bucket}-${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Download failed';
      alert(`Export failed: ${msg}`);
    } finally {
      setDownloading(null);
    }
  };

  const items = [
    { key: 'due', label: 'Due Payments', desc: 'Pending & overdue', cls: 'text-rose-700 hover:bg-rose-50' },
    { key: 'upcoming', label: 'Upcoming Payments', desc: 'Pending & future-due', cls: 'text-amber-700 hover:bg-amber-50' },
    { key: 'made', label: 'Payments Made', desc: 'All paid records', cls: 'text-emerald-700 hover:bg-emerald-50' },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={!!downloading}
        className="flex items-center gap-2 px-3 py-2 border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl text-sm font-medium transition disabled:opacity-60"
        data-testid="payments-export-dropdown"
      >
        {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        {downloading ? `Building ${downloading}.xlsx…` : 'Download Excel'}
        <ChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => handleExport(it.key)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-start gap-2 ${it.cls}`}
              data-testid={`payments-export-${it.key}`}
            >
              <Download size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">{it.label}</div>
                <div className="text-[11px] text-gray-500">{it.desc}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Payments() {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const canEdit = canUserEdit(user, '/stores/payments');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoadError, setPaymentsLoadError] = useState(null);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsHasMore, setPaymentsHasMore] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPayment, setExpandedPayment] = useState(null);
  // For invoice-aggregated payments with >1 linked GRN — tracks which GRN's
  // details to display in the expanded card. Keyed by paymentId. Defaults
  // to the first linked GRN; user can click another chip to switch.
  const [selectedLinkedGrn, setSelectedLinkedGrn] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('due'); // 'due', 'upcoming', 'paid'
  const [upcomingWeek, setUpcomingWeek] = useState(0); // 0 = All, 1, 2, 3, 4

  // Missing-payment self-heal banner
  const [missingAudit, setMissingAudit] = useState(null);
  const [missingExpanded, setMissingExpanded] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  
  // Master data
  const [vendors, setVendors] = useState([]);
  const [grns, setGrns] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    paymentNumber: '',
    vendorId: '',
    vendorName: '',
    grnNo: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    invoiceAmount: '',
    dueDate: '',
    paymentDate: '',
    paymentAmount: '',
    paymentMode: 'NEFT',
    bankName: '',
    referenceNumber: '',
    status: 'Pending',
    remarks: ''
  });

  useEffect(() => {
    loadPayments();
    loadMasterData();
    loadMissingAudit();
  }, []);

  const loadMissingAudit = async () => {
    try {
      const r = await axios.get(`${API}/stores/payments/missing-audit`, { timeout: 45000 });
      setMissingAudit(r.data);
    } catch (e) {
      // Silent — don't break the page if audit fails
      setMissingAudit(null);
    }
  };

  const handleBackfillMissing = async () => {
    const total = missingAudit?.total || 0;
    if (!total) return;
    if (!window.confirm(
      `Create payment entries for ${total} QA-approved GRN${total === 1 ? '' : 's'} that are currently missing payments?\n\n` +
      `This is safe & idempotent — already-paid GRNs are skipped. Each payment is generated using the linked PO's terms (or Net 30 if no PO).`
    )) return;
    setBackfilling(true);
    try {
      const r = await axios.post(`${API}/stores/payments/backfill-missing`);
      const created = r.data?.created || 0;
      const failed = (r.data?.failed || []).length;
      alert(
        `Backfill complete.\n\nCreated: ${created}\nFailed: ${failed}` +
        (failed ? `\n\nFailed GRNs (first 5): ${(r.data?.failed || []).slice(0, 5).join(', ')}` : '')
      );
      await Promise.all([loadPayments(), loadMissingAudit()]);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const loadPayments = async (overridePage) => {
    setLoading(true);
    setPaymentsLoadError(null);
    const targetPage = overridePage || paymentsPage;
    try {
      // limit=500 — user asked the page to show "full numbers in cards with
      // full lists up to 500". This loads up to 500 records in one shot so
      // every tab (Due / Upcoming / Paid) and every stats card reflect the
      // ENTIRE Stores Payments collection, not just the page-1 slice.
      const response = await axios.get(`${API}/stores/payments`, {
        params: { page: targetPage, limit: 500 },
        timeout: 60000,
      });
      const payload = response.data || {};
      const list = Array.isArray(payload) ? payload : (payload.data || []);
      setPayments(list);
      setPaymentsTotal(Array.isArray(payload) ? list.length : (payload.total || list.length));
      setPaymentsHasMore(Array.isArray(payload) ? false : !!payload.hasMore);
      setPaymentsPage(targetPage);
    } catch (error) {
      console.error('Error loading payments:', error);
      if (error?.response?.status !== 401) setPaymentsLoadError(error);
    }
    setLoading(false);
  };

  const loadMasterData = async () => {
    try {
      const [vendorsRes, grnsRes] = await Promise.all([
        axios.get(`${API}/master-data/stores/vendors`),
        axios.get(`${API}/stores/grn`, { params: { limit: 500 } })
      ]);
      setVendors(vendorsRes.data.data || []);
      // GRN endpoint may return either flat array (legacy) or {data, total} envelope
      const grnPayload = grnsRes.data;
      setGrns(Array.isArray(grnPayload) ? grnPayload : (grnPayload?.data || []));
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const generatePaymentNumber = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const count = payments.length + 1;
    return `PAY-${year}${month}-${String(count).padStart(3, '0')}`;
  };

  // Calculate date ranges for filtering
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const getWeekRange = (weekNum) => {
    const startDays = (weekNum - 1) * 7;
    const endDays = weekNum * 7;
    const start = new Date(today);
    start.setDate(start.getDate() + startDays);
    const end = new Date(today);
    end.setDate(end.getDate() + endDays - 1);
    return { start, end };
  };

  // Helper: derive the new paymentStatus values (with fallback to legacy status).
  // This unifies advance-split rows (which carry paymentStatus) with legacy
  // rows (which still drive off `status`).
  const REFUND_STATUSES = ['Refund Pending', 'Refund Received'];
  const isRefundRow = (p) => REFUND_STATUSES.includes(p.paymentStatus) || REFUND_STATUSES.includes(p.status);
  const isHiddenRow = (p) => p.paymentStatus === 'Hidden';
  const isPaidRow = (p) => (p.paymentStatus === 'Paid' || p.status === 'Paid') && !isRefundRow(p);

  // Filter payments based on active tab
  const filterPayments = () => {
    let filtered = payments;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.grnNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.paymentNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.poNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Hidden advance rows are never shown until they graduate to Upcoming
    filtered = filtered.filter(p => !isHiddenRow(p));
    
    // Apply tab filter
    if (activeTab === 'due') {
      filtered = filtered.filter(p => {
        if (isRefundRow(p) || isPaidRow(p)) return false;
        // Advance-split rows: paymentStatus drives the bucket
        if (p.paymentStatus === 'Due') return true;
        if (p.paymentStatus === 'Upcoming') return false;
        // Legacy: dueDate ≤ today
        if (p.status === 'Paid') return false;
        const dueDate = new Date(p.dueDate);
        return dueDate <= today;
      });
    } else if (activeTab === 'upcoming') {
      filtered = filtered.filter(p => {
        if (isRefundRow(p) || isPaidRow(p)) return false;
        if (p.paymentStatus === 'Upcoming') {
          // Week filtering for advance rows (use dueDate if available)
          if (upcomingWeek > 0 && p.dueDate) {
            const dueDate = new Date(p.dueDate);
            const { start, end } = getWeekRange(upcomingWeek);
            return dueDate >= start && dueDate <= end;
          }
          return true;
        }
        if (p.paymentStatus === 'Due') return false;
        if (p.status === 'Paid') return false;
        const dueDate = new Date(p.dueDate);
        if (dueDate <= today) return false;
        if (upcomingWeek > 0) {
          const { start, end } = getWeekRange(upcomingWeek);
          return dueDate >= start && dueDate <= end;
        }
        return true;
      });
    } else if (activeTab === 'paid') {
      filtered = filtered.filter(p => isPaidRow(p));
    } else if (activeTab === 'refunds') {
      filtered = filtered.filter(p => isRefundRow(p));
    }
    
    return filtered;
  };

  // ---- Single source of truth for the amount displayed in each tab. ----
  // Prevents the summary card ↔ list-footer drift bug where the two used
  // different fields (`invoiceAmount` vs `paymentAmount`).
  //   - Due / Upcoming / Refunds → invoice value
  //   - Payments Made (Paid)     → actually-paid amount, fallback to invoice
  //                                for legacy rows where paymentAmount is null
  const rowAmountFor = (p, tab) => {
    if (tab === 'paid') {
      const paid = parseFloat(p.paymentAmount);
      if (!Number.isNaN(paid) && paid > 0) return paid;
      return parseFloat(p.invoiceAmount) || 0;
    }
    return parseFloat(p.invoiceAmount) || 0;
  };

  // Stats calculation
  const _visible = payments.filter(p => !isHiddenRow(p));
  const stats = {
    due: _visible.filter(p => {
      if (isRefundRow(p) || isPaidRow(p)) return false;
      if (p.paymentStatus === 'Due') return true;
      if (p.paymentStatus === 'Upcoming') return false;
      if (p.status === 'Paid') return false;
      const dueDate = new Date(p.dueDate);
      return dueDate <= today;
    }),
    upcoming: _visible.filter(p => {
      if (isRefundRow(p) || isPaidRow(p)) return false;
      if (p.paymentStatus === 'Upcoming') return true;
      if (p.paymentStatus === 'Due') return false;
      if (p.status === 'Paid') return false;
      const dueDate = new Date(p.dueDate);
      return dueDate > today;
    }),
    paid: _visible.filter(p => isPaidRow(p)),
    refunds: _visible.filter(p => isRefundRow(p)),
  };

  const dueAmount = stats.due.reduce((sum, p) => sum + rowAmountFor(p, 'due'), 0);
  const upcomingAmount = stats.upcoming.reduce((sum, p) => sum + rowAmountFor(p, 'upcoming'), 0);
  const paidAmount = stats.paid.reduce((sum, p) => sum + rowAmountFor(p, 'paid'), 0);
  const refundsAmount = stats.refunds.reduce((sum, p) => sum + rowAmountFor(p, 'refunds'), 0);

  const handleVendorSelect = (vendorId, vendor) => {
    setFormData(prev => ({
      ...prev,
      vendorId,
      vendorName: vendor.vendorName || ''
    }));
  };

  const handleGRNSelect = (grnNo, grn) => {
    setFormData(prev => ({
      ...prev,
      grnNo,
      vendorName: grn.supplier || grn.vendorName || prev.vendorName,
      invoiceNumber: grn.invoiceNo || '',
      invoiceAmount: grn.amount || grn.netWeight * (grn.rate || 0) || ''
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePayment = async () => {
    setSaveStatus('saving');
    
    const paymentData = {
      ...formData,
      id: formData.paymentNumber || generatePaymentNumber(),
      paymentNumber: formData.paymentNumber || generatePaymentNumber(),
      createdAt: new Date().toISOString()
    };

    try {
      if (selectedPayment) {
        await axios.put(`${API}/stores/payments/${selectedPayment.id}`, paymentData);
        setMessage({ type: 'success', text: 'Payment updated successfully!' });
      } else {
        await axios.post(`${API}/stores/payments`, paymentData);
        setMessage({ type: 'success', text: 'Payment created successfully!' });
      }
      
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        resetForm();
        loadPayments();
      }, 1500);
    } catch (error) {
      console.error('Error saving payment:', error);
      setSaveStatus('error');
      setMessage({ type: 'error', text: 'Failed to save payment' });
    }
  };

  const handleMarkAsPaid = async (payment) => {
    // Show a quick confirm and use the dedicated mark-paid endpoint
    const confirmPaid = window.confirm(`Mark payment ${payment.paymentNumber || payment.id} as Paid?\n\nAmount: ₹${parseFloat(payment.invoiceAmount || 0).toLocaleString()}\nVendor: ${payment.vendorName || '-'}`);
    
    if (!confirmPaid) return;
    
    try {
      await axios.put(`${API}/stores/payments/${payment.id}/mark-paid`, {
        paymentDate: new Date().toISOString().split('T')[0],
        paymentAmount: payment.invoiceAmount,
        paymentMode: 'NEFT',
        remarks: 'Marked as paid'
      });
      setMessage({ type: 'success', text: 'Payment marked as Paid!' });
      loadPayments();
    } catch (error) {
      console.error('Error updating payment:', error);
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Failed to update payment' });
    }
  };

  // ===== Refund modal (Path B — Admin refund of any payment row) =====
  // Editable refund amount + required reason. Mirrors ProjectPayments UX.
  const [refundModal, setRefundModal] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refunding, setRefunding] = useState(false);

  const openRefundModal = (payment) => {
    const paid = parseFloat(payment.paymentAmount) || parseFloat(payment.paidAmount) || parseFloat(payment.invoiceAmount) || parseFloat(payment.amount) || 0;
    setRefundForm({ amount: paid, reason: '' });
    setRefundModal(payment);
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    if (!refundModal) return;
    const amt = parseFloat(refundForm.amount);
    const cap = parseFloat(refundModal.paymentAmount) || parseFloat(refundModal.paidAmount) || parseFloat(refundModal.invoiceAmount) || parseFloat(refundModal.amount) || 0;
    if (!amt || amt <= 0) { setMessage({ type: 'error', text: 'Enter a valid refund amount' }); return; }
    if (amt > cap + 0.01) { setMessage({ type: 'error', text: `Refund cannot exceed paid amount ₹${cap.toLocaleString()}` }); return; }
    if (!refundForm.reason.trim()) { setMessage({ type: 'error', text: 'Refund reason is required' }); return; }
    setRefunding(true);
    try {
      await axios.post(`${API}/stores/payments/${refundModal.id}/admin-refund`, {
        refundAmount: amt,
        reason: refundForm.reason.trim(),
      });
      setMessage({ type: 'success', text: `Payment moved to Refunds — ₹${amt.toLocaleString()}` });
      setRefundModal(null);
      setRefundForm({ amount: '', reason: '' });
      loadPayments();
    } catch (error) {
      console.error('Admin refund failed:', error);
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Failed to initiate refund' });
    } finally {
      setRefunding(false);
    }
  };

  // Admin-only Path B refund: send any payment row to Refunds tab
  const handleAdminRefund = (payment) => openRefundModal(payment);

  // ===== Bulk Mark-as-Paid (Due Payments tab only) =====
  // Additive feature: reuses the exact single-row mark-paid endpoint per
  // selected payment id. No new backend write path. Selection is scoped to
  // the currently visible (filtered) rows of the Due tab and clears on tab
  // switch, page navigation, search change, and after a completed batch.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkResults, setBulkResults] = useState(null); // { success: [], failed: [{payment, reason}] }

  const clearBulkSelection = () => setSelectedIds(new Set());

  // Safety cap: at most 50 payments per bulk action (financial records).
  const BULK_CAP = 50;
  const isOverBulkCap = selectedIds.size > BULK_CAP;

  const isBulkSelectable = (p) =>
    p.paymentStatus !== 'Refund Pending' &&
    p.paymentStatus !== 'Refund Received' &&
    p.paymentStatus !== 'Paid' &&
    p.status !== 'Paid';

  const toggleBulkRow = (payment) => {
    if (!isBulkSelectable(payment)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(payment.id)) next.delete(payment.id); else next.add(payment.id);
      return next;
    });
  };

  const selectedPayments = payments.filter(p => selectedIds.has(p.id));
  const selectedTotal = selectedPayments.reduce((sum, p) => sum + (parseFloat(p.invoiceAmount) || 0), 0);

  const runBulkMarkPaid = async () => {
    const items = selectedPayments;
    if (items.length === 0) return;
    // Enforce the cap again at execution time — never trust the UI alone.
    if (items.length > BULK_CAP) {
      setMessage({ type: 'error', text: `You can mark at most ${BULK_CAP} payments at once. Deselect ${items.length - BULK_CAP} to continue.` });
      return;
    }
    setBulkRunning(true);
    setBulkResults(null);
    setBulkProgress({ done: 0, total: items.length });
    const success = [];
    const failed = [];
    const today = new Date().toISOString().split('T')[0];
    // Sequential, per-item calls to the EXISTING single mark-paid endpoint.
    // Failures are independent — no rollback of prior successes.
    for (const p of items) {
      try {
        await axios.put(`${API}/stores/payments/${p.id}/mark-paid`, {
          paymentDate: today,
          paymentAmount: p.invoiceAmount,
          paymentMode: 'NEFT',
          remarks: 'Marked as paid'
        });
        success.push(p);
      } catch (err) {
        failed.push({
          payment: p,
          reason: err?.response?.data?.detail || err?.message || 'Unknown error'
        });
      }
      setBulkProgress({ done: success.length + failed.length, total: items.length });
    }
    setBulkResults({ success, failed });
    setBulkRunning(false);
    loadPayments(); // refresh list, tab counts, and summary cards
    if (failed.length === 0) {
      setMessage({ type: 'success', text: `${success.length} of ${items.length} marked as paid successfully.` });
    } else {
      setMessage({ type: 'error', text: `${success.length} of ${items.length} marked as paid; ${failed.length} failed — see details in modal.` });
    }
  };

  // Cancel (pre-confirm): close modal, KEEP selection — no side effects.
  // Close (post-results): batch is done, so also clear the selection.
  const closeBulkModal = () => {
    if (bulkRunning) return;
    setBulkModal(false);
    const finished = !!bulkResults;
    setBulkResults(null);
    if (finished) clearBulkSelection();
  };

  const switchTab = (tab) => {
    if (tab !== activeTab) clearBulkSelection();
    setActiveTab(tab);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    clearBulkSelection();
  };

  const handlePageNav = (page) => {
    clearBulkSelection();
    loadPayments(page);
  };

  // Mark a refund-pending row as received (refund landed in our bank)
  const handleMarkRefundReceived = async (payment) => {
    const bankRef = window.prompt(
      `Mark refund as received for ${payment.paymentNumber || payment.id}?\n\n` +
      `Expected refund: ₹${parseFloat(payment.invoiceAmount || 0).toLocaleString()}\n\n` +
      `Enter bank reference / UTR:`,
      ''
    );
    if (bankRef === null) return;
    try {
      await axios.post(`${API}/stores/payments/${payment.id}/mark-refund-received`, {
        bankRef: bankRef || '',
      });
      setMessage({ type: 'success', text: 'Refund marked as received.' });
      loadPayments();
    } catch (error) {
      console.error('Mark refund received failed:', error);
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Failed to mark refund received' });
    }
  };

  const resetForm = () => {
    setFormData({
      paymentNumber: '',
      vendorId: '',
      vendorName: '',
      grnNo: '',
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceAmount: '',
      dueDate: '',
      paymentDate: '',
      paymentAmount: '',
      paymentMode: 'NEFT',
      bankName: '',
      referenceNumber: '',
      status: 'Pending',
      remarks: ''
    });
    setSelectedPayment(null);
  };

  const openEditModal = (payment) => {
    setSelectedPayment(payment);
    setFormData({
      ...payment,
      paymentNumber: payment.paymentNumber || payment.id
    });
    setShowAddModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysOverdue = (dueDate) => {
    const due = new Date(dueDate);
    const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const filteredPayments = filterPayments();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
              <CreditCard className="text-white" size={24} />
            </div>
            Stores Payments
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage vendor payments and track dues</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadPayments}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
          </button>
          <button
            onClick={() => window.open(`${API}/stores/payments/export-csv`, '_blank')}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium"
            data-testid="export-csv-btn"
            title="Export CSV"
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={() => window.open(`${API}/stores/payments/export-pdf`, '_blank')}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium"
            data-testid="export-pdf-btn"
            title="Export PDF"
          >
            <FileText size={16} />
            PDF
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Recompute all payment amounts to include GST based on each item's HSN code?\n\nThis updates invoiceAmount on auto-generated payments using the HSN master GST rates. Safe & idempotent.")) return;
              try {
                const r = await axios.post(`${API}/stores/payments/recompute-gst`);
                alert(r.data?.message || 'Done');
                loadPayments();
              } catch (e) {
                alert(e?.response?.data?.detail || 'Failed');
              }
            }}
            className="flex items-center gap-2 px-3 py-2 border border-indigo-300 text-indigo-700 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition text-sm font-medium"
            data-testid="recompute-gst-btn"
            title="Recompute payment amounts to include GST from each item's HSN"
          >
            <FileText size={16} />
            Apply GST
          </button>
          <ExportPaymentsDropdown />
          <button
            onClick={() => navigate('/stores/payments/health')}
            className={`flex items-center gap-2 px-3 py-2 border rounded-xl text-sm font-medium transition ${
              (missingAudit?.total || 0) > 0
                ? 'border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100'
                : 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
            }`}
            data-testid="payments-health-badge"
            title="View payment integrity & diversion report"
          >
            <ShieldAlert size={16} />
            Payment Health
            {(missingAudit?.total || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full">
                {missingAudit.total}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              resetForm();
              setFormData(prev => ({ ...prev, paymentNumber: generatePaymentNumber() }));
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition font-medium"
            data-testid="add-payment-btn"
          >
            <Plus size={18} />
            Add Payment
          </button>
        </div>
      </div>

      {/* Missing-payment inline banner removed — moved to dedicated Payment
          Health page (linked via the badge above). */}
      {false && missingAudit && missingAudit.total > 0 && (
        <div
          className="bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 rounded-xl p-4"
          data-testid="missing-payments-banner"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <AlertCircle className="text-rose-600" size={20} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-rose-900">
                  {missingAudit.total} QA-approved GRN{missingAudit.total === 1 ? '' : 's'} missing payment{missingAudit.total === 1 ? '' : 's'}
                </h3>
                <p className="text-xs text-rose-700 mt-0.5">
                  These GRNs have an approved/passed QA but no payment row was created. Run Backfill to generate them — safe & idempotent.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMissingExpanded((p) => !p)}
                className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 bg-white rounded-lg hover:bg-rose-50"
                data-testid="missing-payments-toggle"
              >
                {missingExpanded ? 'Hide details' : `View ${missingAudit.total} GRN${missingAudit.total === 1 ? '' : 's'}`}
              </button>
              <button
                onClick={handleBackfillMissing}
                disabled={backfilling || !canEdit}
                className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-60 flex items-center gap-1.5"
                data-testid="missing-payments-backfill"
                title={!canEdit ? 'Edit access required' : 'Create missing payments'}
              >
                {backfilling ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {backfilling ? 'Backfilling…' : 'Backfill now'}
              </button>
            </div>
          </div>
          {missingExpanded && (
            <div className="mt-3 max-h-60 overflow-y-auto bg-white border border-rose-100 rounded-lg" data-testid="missing-payments-list">
              <table className="w-full text-xs">
                <thead className="bg-rose-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-rose-900 font-semibold">GRN #</th>
                    <th className="px-2 py-1.5 text-left text-rose-900 font-semibold">Material</th>
                    <th className="px-2 py-1.5 text-left text-rose-900 font-semibold">Supplier</th>
                    <th className="px-2 py-1.5 text-left text-rose-900 font-semibold">Invoice</th>
                    <th className="px-2 py-1.5 text-left text-rose-900 font-semibold">QA Approved</th>
                    <th className="px-2 py-1.5 text-right text-rose-900 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-50">
                  {(missingAudit.grns || []).slice(0, 100).map((g) => (
                    <tr key={g.grnNo} className="hover:bg-rose-50/40">
                      <td className="px-2 py-1.5 font-mono text-rose-700">{g.grnNo}</td>
                      <td className="px-2 py-1.5">{g.materialName || '—'}</td>
                      <td className="px-2 py-1.5">{g.supplier || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-500">{g.invoiceNo || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-500">{g.qaApprovalDate || '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{g.amount ? `₹${Number(g.amount).toLocaleString('en-IN')}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {missingAudit.grns && missingAudit.grns.length > 100 && (
                <p className="px-3 py-1.5 text-[11px] text-gray-500">Showing first 100 of {missingAudit.grns.length}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          className={`p-4 rounded-xl border-2 cursor-pointer transition ${
            activeTab === 'due' 
              ? 'bg-red-50 border-red-300' 
              : 'bg-white border-gray-200 hover:border-red-200'
          }`}
          onClick={() => setActiveTab('due')}
          data-testid="stat-card-due"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Due Payments</p>
              <p className="text-2xl font-bold text-red-600">{stats.due.length}</p>
              <p className="text-xs text-red-500 mt-1">{formatCurrency(dueAmount)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <AlertCircle className="text-red-600" size={24} />
            </div>
          </div>
        </div>

        <div 
          className={`p-4 rounded-xl border-2 cursor-pointer transition ${
            activeTab === 'upcoming' 
              ? 'bg-amber-50 border-amber-300' 
              : 'bg-white border-gray-200 hover:border-amber-200'
          }`}
          onClick={() => setActiveTab('upcoming')}
          data-testid="stat-card-upcoming"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Upcoming Payments</p>
              <p className="text-2xl font-bold text-amber-600">{stats.upcoming.length}</p>
              <p className="text-xs text-amber-500 mt-1">{formatCurrency(upcomingAmount)}</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-full">
              <Clock className="text-amber-600" size={24} />
            </div>
          </div>
        </div>

        <div 
          className={`p-4 rounded-xl border-2 cursor-pointer transition ${
            activeTab === 'paid' 
              ? 'bg-green-50 border-green-300' 
              : 'bg-white border-gray-200 hover:border-green-200'
          }`}
          onClick={() => setActiveTab('paid')}
          data-testid="stat-card-paid"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Payments Made</p>
              <p className="text-2xl font-bold text-green-600">{stats.paid.length}</p>
              <p className="text-xs text-green-500 mt-1">{formatCurrency(paidAmount)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div
          className={`p-4 rounded-xl border-2 cursor-pointer transition ${
            activeTab === 'refunds'
              ? 'bg-violet-50 border-violet-300'
              : 'bg-white border-gray-200 hover:border-violet-200'
          }`}
          onClick={() => setActiveTab('refunds')}
          data-testid="stat-card-refunds"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Refunds</p>
              <p className="text-2xl font-bold text-violet-600">{stats.refunds.length}</p>
              <p className="text-xs text-violet-500 mt-1">{formatCurrency(refundsAmount)}</p>
            </div>
            <div className="p-3 bg-violet-100 rounded-full">
              <RotateCcw className="text-violet-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Tab Headers */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => switchTab('due')}
              className={`px-6 py-3 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === 'due'
                  ? 'text-red-600 border-b-2 border-red-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-due"
            >
              <AlertCircle size={16} />
              Due Payments
              {stats.due.length > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs">
                  {stats.due.length}
                </span>
              )}
            </button>
            <button
              onClick={() => switchTab('upcoming')}
              className={`px-6 py-3 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === 'upcoming'
                  ? 'text-amber-600 border-b-2 border-amber-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-upcoming"
            >
              <Clock size={16} />
              Upcoming Payments
              {stats.upcoming.length > 0 && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full text-xs">
                  {stats.upcoming.length}
                </span>
              )}
            </button>
            <button
              onClick={() => switchTab('paid')}
              className={`px-6 py-3 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === 'paid'
                  ? 'text-green-600 border-b-2 border-green-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-paid"
            >
              <CheckCircle size={16} />
              Payments Made
              {stats.paid.length > 0 && (
                <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-full text-xs">
                  {stats.paid.length}
                </span>
              )}
            </button>
            <button
              onClick={() => switchTab('refunds')}
              className={`px-6 py-3 text-sm font-medium transition flex items-center gap-2 ${
                activeTab === 'refunds'
                  ? 'text-violet-600 border-b-2 border-violet-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-refunds"
            >
              <RotateCcw size={16} />
              Refunds
              {stats.refunds.length > 0 && (
                <span className="px-2 py-0.5 bg-violet-100 text-violet-600 rounded-full text-xs">
                  {stats.refunds.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Upcoming Week Sub-tabs */}
        {activeTab === 'upcoming' && (
          <div className="border-b border-gray-100 bg-amber-50/30 px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 mr-2">Filter by week:</span>
              {/* All button */}
              <button
                onClick={() => setUpcomingWeek(0)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  upcomingWeek === 0
                    ? 'bg-amber-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-amber-100 border border-gray-200'
                }`}
              >
                All
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                  upcomingWeek === 0 ? 'bg-amber-600' : 'bg-amber-100 text-amber-600'
                }`}>
                  {stats.upcoming.length}
                </span>
              </button>
              {[1, 2, 3, 4].map(week => {
                const { start, end } = getWeekRange(week);
                const weekPayments = payments.filter(p => {
                  if (p.status === 'Paid') return false;
                  const dueDate = new Date(p.dueDate);
                  return dueDate > today && dueDate >= start && dueDate <= end;
                });
                return (
                  <button
                    key={week}
                    onClick={() => setUpcomingWeek(week)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      upcomingWeek === week
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-gray-600 hover:bg-amber-100 border border-gray-200'
                    }`}
                  >
                    Week {week}
                    {weekPayments.length > 0 && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                        upcomingWeek === week ? 'bg-amber-600' : 'bg-amber-100 text-amber-600'
                      }`}>
                        {weekPayments.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {upcomingWeek > 0 && (
              <p className="text-[10px] text-gray-400 mt-1">
                Week {upcomingWeek}: {formatDate(getWeekRange(upcomingWeek).start)} - {formatDate(getWeekRange(upcomingWeek).end)}
              </p>
            )}
          </div>
        )}

        {/* Bulk action bar — Due tab only, visible when ≥1 row selected */}
        {activeTab === 'due' && selectedIds.size > 0 && (
          <div
            className="sticky top-0 z-10 px-4 py-2.5 border-b border-green-200 bg-green-50 flex flex-wrap items-center gap-3"
            data-testid="bulk-action-bar"
          >
            <span className="text-sm font-medium text-green-900" data-testid="bulk-selected-count">
              {selectedIds.size} payment{selectedIds.size === 1 ? '' : 's'} selected
            </span>
            <span className="text-sm text-green-800" data-testid="bulk-selected-total">
              · Total {formatCurrency(selectedTotal)}
            </span>
            {isOverBulkCap && (
              <span className="text-sm font-medium text-red-700" data-testid="bulk-cap-warning">
                · Max {BULK_CAP} per action — deselect {selectedIds.size - BULK_CAP}
              </span>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setBulkModal(true)}
              disabled={isOverBulkCap}
              title={isOverBulkCap ? `You can mark at most ${BULK_CAP} payments at once` : undefined}
              className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bulk-mark-paid-btn"
            >
              <CheckCircle size={14} /> Mark as Paid
            </button>
            <button
              type="button"
              onClick={clearBulkSelection}
              className="px-3 py-1.5 text-sm text-green-800 hover:bg-green-100 rounded-lg transition"
              data-testid="bulk-clear-selection-btn"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg w-full max-w-md">
            <Search size={18} className="text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by vendor, invoice, GRN..."
              className="flex-1 bg-transparent border-none outline-none text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <ListLoadError
          error={paymentsLoadError}
          busy={loading}
          onRetry={() => loadPayments()}
          label="payments"
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {activeTab === 'due' && (() => {
                  const visibleSelectable = filteredPayments.filter(isBulkSelectable);
                  const allSelected = visibleSelectable.length > 0 && visibleSelectable.every(p => selectedIds.has(p.id));
                  return (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (allSelected) {
                              visibleSelectable.forEach(p => next.delete(p.id));
                            } else {
                              visibleSelectable.forEach(p => next.add(p.id));
                            }
                            return next;
                          });
                        }}
                        className="rounded accent-green-600 cursor-pointer"
                        title="Select all on this page"
                        data-testid="bulk-select-all-checkbox"
                      />
                    </th>
                  );
                })()}
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Payment #</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Vendor</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">GRN / Invoice</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">GRN Done By</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-600">Amount</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Due Date</th>
                {activeTab === 'due' && (
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Overdue</th>
                )}
                {activeTab === 'paid' && (
                  <>
                    <th className="px-4 py-3 text-center font-semibold text-gray-600">Paid Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Reference</th>
                  </>
                )}
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-gray-500">
                    No payments found in this category
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <React.Fragment key={payment.id}>
                  <tr className={`hover:bg-gray-50 transition cursor-pointer ${expandedPayment === payment.id ? 'bg-amber-50' : ''} ${selectedIds.has(payment.id) ? 'bg-green-50/60' : ''}`}
                      onClick={() => setExpandedPayment(expandedPayment === payment.id ? null : payment.id)}>
                    {activeTab === 'due' && (
                      <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(payment.id)}
                          onChange={() => toggleBulkRow(payment)}
                          disabled={!isBulkSelectable(payment)}
                          className="rounded accent-green-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`bulk-select-checkbox-${payment.id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono text-green-700 font-medium">
                      <div className="flex items-center gap-1.5">
                        <ChevronRight size={14} className={`text-gray-400 transition-transform ${expandedPayment === payment.id ? 'rotate-90' : ''}`} />
                        {payment.paymentNumber || payment.id}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{payment.vendorName || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 flex items-center gap-1.5 flex-wrap">
                        {payment.aggregationMode === 'invoice' && Array.isArray(payment.linkedGrns) && payment.linkedGrns.length > 1 ? (
                          <>
                            <span>{payment.linkedGrns[0]}</span>
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium cursor-help"
                              title={`Multi-GRN invoice payment\nLinked GRNs:\n${payment.linkedGrns.join('\n')}`}
                              data-testid={`payment-multi-grn-badge-${payment.id}`}
                            >
                              <FileText size={10} />
                              +{payment.linkedGrns.length - 1} more
                            </span>
                          </>
                        ) : (
                          payment.grnNo || '-'
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{payment.invoiceNumber || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-800">{payment.grnDetails?.createdBy || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold" data-testid={`payment-row-amount-${payment.id}`}>
                      {formatCurrency(rowAmountFor(payment, activeTab))}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {formatDate(payment.dueDate)}
                    </td>
                    {activeTab === 'due' && (
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          {getDaysOverdue(payment.dueDate)} days
                        </span>
                      </td>
                    )}
                    {activeTab === 'paid' && (
                      <>
                        <td className="px-4 py-3 text-center">
                          {formatDate(payment.paymentDate)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {payment.referenceNumber || '-'}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-center">
                      {payment.paymentStatus === 'Refund Pending' ? (
                        <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium" data-testid={`status-refund-pending-${payment.id}`}>
                          Refund Pending
                        </span>
                      ) : payment.paymentStatus === 'Refund Received' ? (
                        <span className="px-2 py-1 bg-violet-200 text-violet-800 rounded-full text-xs font-medium" data-testid={`status-refund-received-${payment.id}`}>
                          Refund Received
                        </span>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'Paid'
                            ? 'bg-green-100 text-green-700'
                            : payment.status === 'Pending'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {payment.status || 'Pending'}
                        </span>
                      )}
                      {/* Advance split chip — only on advance-config rows */}
                      {payment.advanceSplitOrder && (
                        <div className="mt-1">
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              payment.paymentType === 'Advance'
                                ? 'bg-rose-100 text-rose-700 border border-rose-200'
                                : 'bg-sky-100 text-sky-700 border border-sky-200'
                            }`}
                            title={`Split ${payment.advanceSplitOrder} of ${payment.advanceTotalSplits} — ${payment.advanceSplitPercent}% ${payment.paymentType === 'Advance' ? 'Advance' : `after ${payment.advanceDaysAfterGRN}d on GRN`}`}
                            data-testid={`split-chip-${payment.id}`}
                          >
                            <Wallet size={9} />
                            {payment.paymentType === 'Advance' ? 'Advance' : `${payment.advanceDaysAfterGRN}d`}
                            <span className="opacity-70">{payment.advanceSplitOrder}/{payment.advanceTotalSplits}</span>
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        {/* Mark Paid — visible only when row is Due (not in refund or already paid) */}
                        {payment.paymentStatus !== 'Refund Pending' &&
                         payment.paymentStatus !== 'Refund Received' &&
                         payment.paymentStatus !== 'Paid' &&
                         payment.status !== 'Paid' && (
                          <button
                            onClick={() => handleMarkAsPaid(payment)}
                            className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition"
                            title="Mark as Paid"
                            data-testid={`mark-paid-btn-${payment.id}`}
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        {/* Admin Payment Refund — Path B. Hidden when already in refund state. */}
                        {user?.role === 'Admin' &&
                         payment.paymentStatus !== 'Refund Pending' &&
                         payment.paymentStatus !== 'Refund Received' && (
                          <button
                            onClick={() => handleAdminRefund(payment)}
                            className="p-1.5 bg-violet-100 text-violet-700 rounded hover:bg-violet-200 transition"
                            title="Admin: move to Refunds"
                            data-testid={`admin-refund-btn-${payment.id}`}
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}
                        {/* Mark Refund Received — only in refund-pending state */}
                        {payment.paymentStatus === 'Refund Pending' && (
                          <button
                            onClick={() => handleMarkRefundReceived(payment)}
                            className="px-2 py-1 bg-violet-600 text-white rounded hover:bg-violet-700 transition text-xs font-medium"
                            title="Mark refund as received in our bank"
                            data-testid={`mark-refund-received-btn-${payment.id}`}
                          >
                            ✓ Received
                          </button>
                        )}
                        {canEdit && (
                        <button
                          onClick={() => openEditModal(payment)}
                          className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded GRN + QA Summary Card */}
                  {expandedPayment === payment.id && (() => {
                    // Resolve which GRN to show in the cards below. For
                    // multi-GRN invoice-aggregated payments, default to
                    // the first linked GRN but let the user switch via
                    // clickable chips above.
                    const linked = Array.isArray(payment.linkedGrns) ? payment.linkedGrns : [];
                    const isMulti = payment.aggregationMode === 'invoice' && linked.length > 1;
                    const activeGrn = isMulti
                      ? (selectedLinkedGrn[payment.id] || linked[0])
                      : null;
                    const perGrn = payment.linkedGrnDetails || {};
                    const grnView = isMulti && perGrn[activeGrn]?.grnDetails
                      ? perGrn[activeGrn].grnDetails
                      : payment.grnDetails;
                    const qaView = isMulti && perGrn[activeGrn]?.qaDetails
                      ? perGrn[activeGrn].qaDetails
                      : payment.qaDetails;
                    return (
                    <tr>
                      <td colSpan={activeTab === 'due' ? 10 : activeTab === 'paid' ? 10 : 8} className="px-4 py-0 bg-amber-50/50">
                        {isMulti && (
                          <div className="mx-2 mt-3 mb-1 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-3" data-testid={`payment-linked-grns-banner-${payment.id}`}>
                            <FileText size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-indigo-900 mb-1">
                                Invoice-aggregated payment · {linked.length} GRNs linked
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {linked.map(g => {
                                  const isActive = g === activeGrn;
                                  return (
                                    <button
                                      key={g}
                                      type="button"
                                      onClick={() => setSelectedLinkedGrn(prev => ({ ...prev, [payment.id]: g }))}
                                      className={`px-2 py-0.5 rounded text-[11px] font-mono border transition ${
                                        isActive
                                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                          : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-400 cursor-pointer'
                                      }`}
                                      data-testid={`payment-${payment.id}-linked-grn-chip-${g}`}
                                      title={isActive ? 'Currently viewing' : `Click to view ${g}`}
                                    >
                                      {g}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-[10px] text-indigo-700/80 mt-1.5 italic">
                                Click any GRN above to view its details below. Invoice total includes all linked GRNs.
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="py-4 px-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* GRN Details Card */}
                          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <FileText size={14} className="text-amber-500" />
                              GRN Details
                            </h4>
                            {grnView ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                  <div>
                                    <span className="text-gray-500 text-xs">GRN Number</span>
                                    <p className="font-mono font-semibold text-gray-800">{grnView.grnNumber}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">Date</span>
                                    <p className="text-gray-800">{grnView.date}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">PO Number</span>
                                    <p className="text-gray-800">{grnView.poNumber || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">Vehicle</span>
                                    <p className="text-gray-800">{grnView.vehicleNo || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">Invoice No</span>
                                    <p className="text-gray-800">{grnView.invoiceNo || '-'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">Invoice Date</span>
                                    <p className="text-gray-800">{grnView.invoiceDate || '-'}</p>
                                  </div>
                                  {grnView.createdBy && (
                                    <div>
                                      <span className="text-gray-500 text-xs">Received By</span>
                                      <p className="text-gray-800 font-medium">{grnView.createdBy}</p>
                                    </div>
                                  )}
                                </div>
                                {/* GRN Items */}
                                {grnView.items?.length > 0 && (
                                  <div className="border-t pt-2">
                                    <p className="text-xs font-semibold text-gray-500 mb-1.5">Items</p>
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-gray-400 border-b">
                                          <th className="py-1 text-left">Material</th>
                                          <th className="py-1 text-right">Qty/Net Wt</th>
                                          <th className="py-1 text-right">Rate</th>
                                          <th className="py-1 text-right">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {grnView.items.map((item, i) => (
                                          <tr key={i} className="border-b border-gray-50">
                                            <td className="py-1.5 text-gray-700">{item.commodityName || '-'}</td>
                                            <td className="py-1.5 text-right">{parseFloat(item.netWeight || item.quantity || 0).toFixed(2)} {item.unit}</td>
                                            <td className="py-1.5 text-right">₹{parseFloat(item.rate || 0).toFixed(2)}</td>
                                            <td className="py-1.5 text-right font-medium">₹{parseFloat(item.amount || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {/* Misc Charges */}
                                {grnView.miscCharges?.length > 0 && (
                                  <div className="border-t pt-2">
                                    <p className="text-xs font-semibold text-gray-500 mb-1">Misc Charges (Non-GST)</p>
                                    {grnView.miscCharges.map((ch, i) => (
                                      <div key={i} className="flex justify-between text-xs text-gray-600">
                                        <span>{ch.description}</span>
                                        <span>₹{parseFloat(ch.amount || 0).toLocaleString('en-IN')}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* GST Breakdown */}
                                {(payment.baseAmount || payment.gstAmount) ? (
                                  <div className="border-t pt-2 mt-1 space-y-0.5" data-testid={`gst-breakdown-${payment.id}`}>
                                    <div className="flex justify-between text-xs text-gray-600">
                                      <span>Base Amount</span>
                                      <span>₹{parseFloat(payment.baseAmount || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-indigo-700">
                                      <span>GST (from HSN)</span>
                                      <span>₹{parseFloat(payment.gstAmount || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}</span>
                                    </div>
                                    <div className="flex justify-between text-xs font-semibold text-gray-900 border-t pt-1">
                                      <span>Invoice Amount (incl. GST)</span>
                                      <span>₹{parseFloat(payment.invoiceAmount || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}</span>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic">GRN details not available</p>
                            )}
                          </div>
                          
                          {/* QA Details Card */}
                          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <CheckCircle size={14} className="text-green-500" />
                              QA / Quality Check Details
                            </h4>
                            {qaView?.length > 0 ? (
                              <div className="space-y-3">
                                {qaView.map((qa, i) => (
                                  <div key={i} className="p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-mono text-xs font-semibold text-gray-700">{qa.qaId}</span>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        qa.status === 'Passed' || qa.status === 'Approved' 
                                          ? 'bg-green-100 text-green-700' 
                                          : qa.status === 'Rejected' 
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {qa.status}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                      <div>
                                        <span className="text-gray-400">Material</span>
                                        <p className="text-gray-700 font-medium">{qa.material}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-400">Quantity</span>
                                        <p className="text-gray-700">{parseFloat(qa.quantity || 0).toFixed(2)} {qa.unit}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-400">Rate</span>
                                        <p className="text-gray-700">₹{parseFloat(qa.rate || 0).toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-400">Inspector</span>
                                        <p className="text-gray-700">{qa.inspector || '-'}</p>
                                      </div>
                                      {qa.inspectionDate && (
                                        <div>
                                          <span className="text-gray-400">Inspection Date</span>
                                          <p className="text-gray-700">{qa.inspectionDate.split('T')[0]}</p>
                                        </div>
                                      )}
                                      {qa.remarks && (
                                        <div className="col-span-2">
                                          <span className="text-gray-400">Remarks</span>
                                          <p className="text-gray-700">{qa.remarks}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic">QA details not available</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    );
                  })()}
                  </React.Fragment>
                ))
              )}
            </tbody>
            {/* Total Footer Row */}
            {filteredPayments.length > 0 && (
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-700" colSpan={2}>
                    Total ({filteredPayments.length} {filteredPayments.length === 1 ? 'payment' : 'payments'})
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900 text-base" data-testid="payments-list-grand-total">
                    {formatCurrency(filteredPayments.reduce((sum, p) => sum + rowAmountFor(p, activeTab), 0))}
                  </td>
                  <td className="px-4 py-3" colSpan={activeTab === 'due' ? 4 : activeTab === 'paid' ? 4 : 2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {paymentsTotal > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs" data-testid="payments-pagination">
            <div className="text-gray-600">
              Showing <span className="font-semibold">{(paymentsPage - 1) * 100 + 1}</span>–
              <span className="font-semibold">{Math.min(paymentsPage * 100, paymentsTotal)}</span> of{' '}
              <span className="font-semibold">{paymentsTotal}</span> payment(s)
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => paymentsPage > 1 && handlePageNav(paymentsPage - 1)}
                disabled={paymentsPage <= 1 || loading}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="payments-prev-page"
              >
                ← Prev
              </button>
              <span className="px-2 text-gray-500">Page {paymentsPage}</span>
              <button
                onClick={() => paymentsHasMore && handlePageNav(paymentsPage + 1)}
                disabled={!paymentsHasMore || loading}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="payments-next-page"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Mark-as-Paid confirmation / results modal */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]" data-testid="bulk-confirm-modal">
            <div className="p-5 border-b border-gray-200 bg-green-600 rounded-t-2xl flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {bulkResults ? 'Bulk Mark-as-Paid Results' : 'Confirm Bulk Mark as Paid'}
                </h2>
                <p className="text-xs text-white/80">
                  {selectedPayments.length} payment{selectedPayments.length === 1 ? '' : 's'} · {formatCurrency(selectedTotal)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeBulkModal}
                disabled={bulkRunning}
                className="text-white/80 hover:bg-white/10 p-2 rounded-lg disabled:opacity-40"
                data-testid="bulk-confirm-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Payment list — scrollable for large selections */}
            <div className="p-4 overflow-y-auto flex-1 min-h-0" data-testid="bulk-confirm-list">
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {selectedPayments.map((p) => {
                  const failure = bulkResults?.failed.find(f => f.payment.id === p.id);
                  const succeeded = bulkResults?.success.some(s => s.id === p.id);
                  return (
                    <div key={p.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`bulk-confirm-item-${p.id}`}>
                      {bulkRunning || bulkResults ? (
                        succeeded
                          ? <CheckCircle size={15} className="text-green-600 shrink-0" />
                          : failure
                            ? <AlertCircle size={15} className="text-red-600 shrink-0" />
                            : <Loader2 size={15} className="animate-spin text-gray-400 shrink-0" />
                      ) : (
                        <CheckCircle size={15} className="text-gray-300 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-green-700 font-medium truncate">{p.paymentNumber || p.id}</div>
                        <div className="text-xs text-gray-500 truncate">{p.vendorName || '-'}</div>
                        {failure && (
                          <div className="text-xs text-red-600 mt-0.5" data-testid={`bulk-failure-item-${p.id}`}>
                            Failed: {failure.reason}
                          </div>
                        )}
                      </div>
                      <div className="font-semibold tabular-nums shrink-0">{formatCurrency(parseFloat(p.invoiceAmount) || 0)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress during run */}
            {bulkRunning && (
              <div className="px-4 pb-2 shrink-0" data-testid="bulk-progress-indicator">
                <div className="text-xs text-gray-600 mb-1">
                  Processing {bulkProgress.done} of {bulkProgress.total}…
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            {/* Results summary */}
            {bulkResults && (
              <div className={`mx-4 mb-3 p-3 rounded-lg text-sm shrink-0 ${
                bulkResults.failed.length === 0
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`} data-testid="bulk-results-summary">
                {bulkResults.success.length} of {bulkResults.success.length + bulkResults.failed.length} marked as paid successfully.
                {bulkResults.failed.length > 0 && (
                  <span className="block mt-1 text-xs">
                    {bulkResults.failed.length} failed — {bulkResults.failed.map(f => f.payment.paymentNumber || f.payment.id).join(', ')}
                  </span>
                )}
              </div>
            )}

            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-between items-center shrink-0">
              <div className="text-sm text-gray-600">
                Total: <span className="font-bold text-gray-900">{formatCurrency(selectedTotal)}</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeBulkModal}
                  disabled={bulkRunning}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm disabled:opacity-40"
                  data-testid="bulk-confirm-cancel-btn"
                >
                  {bulkResults ? 'Close' : 'Cancel'}
                </button>
                {!bulkResults && (
                  <button
                    type="button"
                    onClick={runBulkMarkPaid}
                    disabled={bulkRunning || selectedPayments.length === 0 || isOverBulkCap}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                    data-testid="bulk-confirm-submit-btn"
                  >
                    {bulkRunning ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    {bulkRunning ? 'Processing…' : 'Confirm & Mark as Paid'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Toast */}
      {message && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-2">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Add/Edit Payment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <CreditCard className="text-green-600" size={20} />
                  {selectedPayment ? 'Edit Payment' : 'Add New Payment'}
                </h2>
                <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-1 hover:bg-gray-200 rounded">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Payment Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Payment Number</label>
                  <input
                    type="text"
                    value={formData.paymentNumber}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              </div>

              {/* Vendor Selection */}
              <SearchableDropdown
                label="Vendor"
                value={formData.vendorId}
                options={vendors}
                onChange={handleVendorSelect}
                placeholder="Select vendor..."
                displayField="vendorName"
                valueField="id"
                required
                testId="vendor-dropdown"
              />

              {/* GRN Selection */}
              <SearchableDropdown
                label="GRN Reference"
                value={formData.grnNo}
                options={grns.map(g => ({ ...g, display: `${g.id} - ${g.supplier || g.vendorName || ''}` }))}
                onChange={handleGRNSelect}
                placeholder="Select GRN..."
                displayField="display"
                valueField="id"
                testId="grn-dropdown"
              />

              {/* Invoice Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input
                    type="text"
                    value={formData.invoiceNumber}
                    onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="INV-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={formData.invoiceDate}
                    onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Amount and Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Amount (₹) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={formData.invoiceAmount}
                    onChange={(e) => handleInputChange('invoiceAmount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Due Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Payment Details (when paid) */}
              {formData.status === 'Paid' && (
                <>
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment Details</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date</label>
                      <input
                        type="date"
                        value={formData.paymentDate}
                        onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount (₹)</label>
                      <input
                        type="number"
                        value={formData.paymentAmount}
                        onChange={(e) => handleInputChange('paymentAmount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
                      <select
                        value={formData.paymentMode}
                        onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      >
                        <option value="NEFT">NEFT</option>
                        <option value="RTGS">RTGS</option>
                        <option value="IMPS">IMPS</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Cash">Cash</option>
                        <option value="UPI">UPI</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Reference Number</label>
                      <input
                        type="text"
                        value={formData.referenceNumber}
                        onChange={(e) => handleInputChange('referenceNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        placeholder="Transaction reference"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={formData.bankName}
                      onChange={(e) => handleInputChange('bankName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      placeholder="Bank name"
                    />
                  </div>
                </>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => handleInputChange('remarks', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                  rows={2}
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                disabled={saveStatus === 'saving' || !formData.invoiceAmount || !formData.dueDate}
                className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                  saveStatus === 'saved'
                    ? 'bg-green-500 text-white'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {saveStatus === 'saving' ? (
                  <><Loader2 size={16} className="animate-spin" /> Saving...</>
                ) : saveStatus === 'saved' ? (
                  <><CheckCircle size={16} /> Saved!</>
                ) : (
                  <><Save size={16} /> Save Payment</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Refund modal — editable amount + required reason.
          Mirrors ProjectPayments UX for parity across modules. */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitRefund} className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="stores-refund-modal">
            <div className="p-5 border-b border-gray-200 bg-rose-600 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">Initiate Refund</h2>
                <p className="text-xs text-white/80">{refundModal.paymentNumber || refundModal.id} · {refundModal.vendorName || '-'}</p>
              </div>
              <button
                type="button"
                onClick={() => { setRefundModal(null); setRefundForm({ amount: '', reason: '' }); }}
                className="text-white/80 hover:bg-white/10 p-2 rounded-lg"
                data-testid="stores-refund-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Amount:</span>
                  <span className="font-semibold tabular-nums">
                    ₹{(parseFloat(refundModal.invoiceAmount) || parseFloat(refundModal.amount) || 0).toLocaleString()}
                  </span>
                </div>
                {(parseFloat(refundModal.paymentAmount) || parseFloat(refundModal.paidAmount) || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className="font-semibold tabular-nums">
                      ₹{(parseFloat(refundModal.paymentAmount) || parseFloat(refundModal.paidAmount)).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-rose-600 font-semibold pt-1 border-t border-gray-200">
                  <span>Current Status:</span><span>{refundModal.paymentStatus || refundModal.status}</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">
                  Refund Amount <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-rose-500 outline-none"
                  data-testid="stores-refund-amount-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Pre-filled with paid amount. Reduce for partial refund (e.g. only 30% dispute).
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 uppercase">
                  Refund Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. Material found defective after installation / vendor short-shipped / QA post-approval failure"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-rose-500 outline-none resize-none"
                  data-testid="stores-refund-reason-input"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                <ShieldAlert size={12} className="inline mr-1" />
                Row will move from Completed → Refunds tab as <strong>Refund Pending</strong>.
                Then use <strong>Mark Refund Received</strong> once the vendor returns the money.
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setRefundModal(null); setRefundForm({ amount: '', reason: '' }); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={refunding}
                className="px-5 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center gap-2 disabled:opacity-60"
                data-testid="stores-refund-submit-btn"
              >
                {refunding ? <Loader2 size={16} className="animate-spin" /> : <>Initiate Refund</>}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default Payments;
